<template>
  <div class="about-page mx-auto px-4 px-md-8 pb-8">

    <!-- Hero -->
    <div class="about-hero py-10 text-center">
      <v-img :src="$theme.assets.logoContent" contain height="60" class="mx-auto mb-4" />
      <div class="text-h6 font-weight-bold">{{ $theme.brand.appName }}</div>
      <div class="caption grey--text about-tagline">{{ $theme.brand.tagline }}</div>
      <v-chip small outlined color="primary" class="mt-4">
        <v-icon small class="mr-1" color="primary">mdi-tag-outline</v-icon>
        Version {{ version }}
      </v-chip>
    </div>

    <!-- Cards -->
    <v-row class="about-cards">
      <v-col cols="12" md="6">

        <!-- Documents légaux -->
        <div class="about-section-label overline grey--text text--darken-1 mb-2 px-1">Documents légaux</div>

        <v-card class="about-card rounded-lg" outlined>
          <v-card-text class="pa-0">
            <template v-for="(doc, i) in legalDocs">
              <router-link :key="doc.to" :to="doc.to" class="about-row px-4 py-3">
                <v-icon color="blue darken-1" class="about-row__icon">{{ doc.icon }}</v-icon>
                <div class="about-row__body">
                  <div class="subtitle-2 font-weight-medium">{{ doc.title }}</div>
                  <div class="caption grey--text">{{ doc.subtitle }}</div>
                </div>
                <v-icon small color="grey">mdi-chevron-right</v-icon>
              </router-link>
              <v-divider v-if="i < legalDocs.length - 1" :key="`div-${doc.to}`" />
            </template>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="6">

        <!-- À propos -->
        <div class="about-section-label overline grey--text text--darken-1 mb-2 px-1">À propos</div>

        <v-card class="about-card rounded-lg" outlined>
          <v-card-text class="pa-0">
            <template v-for="(doc, i) in supportLinks">
              <router-link :key="doc.to" :to="doc.to" class="about-row px-4 py-3">
                <v-icon color="blue darken-1" class="about-row__icon">{{ doc.icon }}</v-icon>
                <div class="about-row__body">
                  <div class="subtitle-2 font-weight-medium">{{ doc.title }}</div>
                  <div class="caption grey--text">{{ doc.subtitle }}</div>
                </div>
                <v-icon small color="grey">mdi-chevron-right</v-icon>
              </router-link>
              <v-divider v-if="i < supportLinks.length - 1" :key="`div-${doc.to}`" />
            </template>
            <v-divider />
            <a :href="`mailto:${contactEmail}`" class="about-row px-4 py-3">
              <v-icon color="blue darken-1" class="about-row__icon">mdi-email-outline</v-icon>
              <div class="about-row__body">
                <div class="subtitle-2 font-weight-medium">Nous contacter</div>
                <div class="caption grey--text">{{ contactEmail }}</div>
              </div>
              <v-icon small color="grey">mdi-open-in-new</v-icon>
            </a>
          </v-card-text>
        </v-card>

        <!-- Éditeur -->
        <div class="about-section-label overline grey--text text--darken-1 mt-6 mb-2 px-1">Éditeur</div>

        <v-card class="about-card rounded-lg" outlined>
          <a
            href="https://starkia.fr"
            target="_blank"
            rel="noopener noreferrer"
            class="about-row starkia-row px-4 py-4"
          >
            <v-avatar size="40" class="about-row__icon mr-2">
              <v-img src="/starkia_logo.png" contain />
            </v-avatar>
            <div class="about-row__body">
              <div class="subtitle-2 font-weight-medium starkia-title">STARKIA TECHNOLOGIES</div>
              <div class="caption starkia-subtitle">SAS · Sophia Antipolis, Valbonne (France)</div>
            </div>
            <v-icon small color="grey">mdi-open-in-new</v-icon>
          </a>
        </v-card>
      </v-col>
    </v-row>

  </div>
</template>

<script>
import { version } from '../../package.json'
import features from '@/config/features'

export default {
  name: 'AboutContent',
  data: () => ({
    version,
    contactEmail: 'support@optinego.com',
    legalDocs: [
      {
        to: '/mentions-legales',
        icon: 'mdi-file-certificate-outline',
        title: 'Mentions légales',
        subtitle: 'Éditeur, hébergement, propriété intellectuelle',
      },
      {
        to: '/cgv',
        icon: 'mdi-file-sign',
        title: 'Conditions générales de vente',
        subtitle: 'Modalités contractuelles du service',
      },
      {
        to: '/politique-confidentialite',
        icon: 'mdi-shield-lock-outline',
        title: 'Politique de confidentialité',
        subtitle: 'Traitement de vos données personnelles',
      },
      {
        to: '/politique-cookies',
        icon: 'mdi-cookie-outline',
        title: 'Politique cookies',
        subtitle: 'Cookies et traceurs utilisés par le service',
      },
    ],
    supportLinks: [
      {
        to: '/manuel',
        icon: 'mdi-help-circle-outline',
        title: 'Manuel utilisateur',
        subtitle: 'Documentation complète',
      },
      {
        to: '/faq',
        icon: 'mdi-frequently-asked-questions',
        title: 'Questions fréquentes',
        subtitle: 'Réponses aux questions les plus courantes',
      },
      {
        to: '/support',
        icon: 'mdi-lifebuoy',
        title: 'Centre d\'aide',
        subtitle: 'FAQ, documentation et contact du support',
      },
    ].filter(l => features.support || l.to === '/manuel'),
  }),
}
</script>

<style scoped>
.about-page {
  max-width: 900px;
}

.about-section-label {
  font-size: 10px;
  letter-spacing: 0.12em;
}

.about-card {
  border-color: #e0e4ea !important;
  overflow: hidden;
}

.about-row {
  display: flex;
  align-items: center;
  text-decoration: none;
  color: inherit;
  transition: background 0.15s ease;
}

.about-row:hover {
  background: rgba(0, 0, 0, 0.03);
}

.about-row__icon {
  width: 36px;
  flex-shrink: 0;
}

.about-row__body {
  flex-grow: 1;
  min-width: 0;
}

.starkia-row {
  background: #22262e;
}

.starkia-row:hover {
  background: #2a2f39;
}

.starkia-title {
  color: #ffffff;
}

.starkia-subtitle {
  color: #6cb6f6;
}
</style>
